import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StandardPageLayout from '../components/StandardPageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  GitBranch, 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RefreshCw,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CIStatus = () => {
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchData = async () => {
    try {
      // Fetch runs (axios includes X-API-Key via interceptor)
      const runsRes = await axios.get(`${API_URL}/api/webhook/github/runs?limit=20`);
      setRuns(runsRes.data.runs || []);

      // Fetch status
      const statusRes = await axios.get(`${API_URL}/api/webhook/github/status`);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching CI data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const triggerTests = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API_URL}/api/webhook/github/trigger`);
      if (res.data.status === 'accepted') {
        toast.success('Tests déclenchés avec succès');
        // Refresh after a short delay
        setTimeout(fetchData, 2000);
      }
    } catch (error) {
      toast.error('Erreur lors du déclenchement des tests');
    } finally {
      setTriggering(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'passed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Passé</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" /> Échoué</Badge>;
      case 'running':
        return <Badge className="bg-blue-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> En cours</Badge>;
      case 'timeout':
        return <Badge className="bg-orange-500"><Clock className="w-3 h-3 mr-1" /> Timeout</Badge>;
      default:
        return <Badge className="bg-gray-500"><AlertTriangle className="w-3 h-3 mr-1" /> {status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <StandardPageLayout
        title="CI/CD Status"
        subtitle="Tests automatisés"
        icon={<GitBranch className="w-6 h-6 sm:w-8 sm:h-8 text-slate-700 dark:text-slate-300" />}
      >
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-700 dark:text-slate-300" />
        </div>
      </StandardPageLayout>
    );
  }

  return (
    <StandardPageLayout
      title="CI/CD Status"
      subtitle="Tests automatisés"
      icon={<GitBranch className="w-6 h-6 sm:w-8 sm:h-8 text-slate-700 dark:text-slate-300" />}
    >
      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button 
          onClick={triggerTests} 
          disabled={triggering}
          className="bg-slate-800 hover:bg-slate-700"
          data-testid="trigger-tests-btn"
        >
          {triggering ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          Lancer les tests
        </Button>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Webhook Info */}
      {status && (
        <Card className="mb-6 border-0 shadow-md">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-slate-700 dark:text-slate-300" />
              Configuration Webhook GitHub
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">URL:</span>
                <code className="ml-2 bg-gray-100 px-2 py-1 rounded text-xs">
                  {API_URL}/api/webhook/github
                </code>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Événements:</span>
                <span className="ml-2">{status.supported_events?.join(', ')}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Secret:</span>
                <Badge className={status.secret_configured ? "bg-green-500 ml-2" : "bg-red-500 ml-2"}>
                  {status.secret_configured ? "Configuré" : "Non configuré"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Runs */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="py-4 bg-slate-800 text-white dark:bg-slate-700 rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Exécutions récentes
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <GitBranch className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune exécution de tests</p>
              <p className="text-sm mt-2">Cliquez sur "Lancer les tests" pour commencer</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">ID</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-center">Tests</th>
                    <th className="px-4 py-3 text-center">Durée</th>
                    <th className="px-4 py-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run, index) => (
                    <tr key={run.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-mono text-xs">{run.id}</td>
                      <td className="px-4 py-3">{getStatusBadge(run.status)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-green-600 font-medium">{run.passed}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-gray-600 dark:text-gray-400">{run.total}</span>
                        {run.failed > 0 && (
                          <span className="text-red-600 ml-2">({run.failed} échecs)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {run.duration ? `${run.duration.toFixed(2)}s` : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDate(run.started_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card className="mt-6 border-0 shadow-md">
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Configuration GitHub</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600 dark:text-gray-400">
          <p className="mb-2">Pour configurer le webhook GitHub :</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Allez dans votre repository GitHub → Settings → Webhooks</li>
            <li>Cliquez sur "Add webhook"</li>
            <li>
              Payload URL: <code className="bg-gray-100 px-1 rounded">{API_URL}/api/webhook/github</code>
            </li>
            <li>Content type: <code className="bg-gray-100 px-1 rounded">application/json</code></li>
            <li>Secret: <code className="bg-gray-100 px-1 rounded">mzarati-webhook-secret-2024</code></li>
            <li>Événements: Push, Pull requests</li>
          </ol>
        </CardContent>
      </Card>
    </StandardPageLayout>
  );
};

export default CIStatus;
